import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { clampGridSize } from "../utils/pixelAvatar";

const isProduction =
  process.env.REACT_APP_FIREBASE_ENV === "production" ||
  (process.env.REACT_APP_FIREBASE_ENV !== "development" &&
    process.env.NODE_ENV === "production");

export const PIXEL_POSTS_COLLECTION = isProduction
  ? "production_pixelPosts"
  : "pixelPosts";

const LOCAL_POSTS_KEY = "arcadePixelPosts";
const LOCAL_HEARTS_KEY = "arcadePixelHearts";
const MAX_NEW = 200;
const MAX_POPULAR = 100;
const MAX_ALL = 200;
const NEW_MAX_AGE_MS = 10 * 24 * 60 * 60 * 1000;
export const POPULAR_MIN_HEARTS = 100;

const readLocalPosts = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return [];
    const raw = window.localStorage.getItem(LOCAL_POSTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizePost).filter(Boolean) : [];
  } catch {
    return [];
  }
};

const writeLocalPosts = (posts) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(LOCAL_POSTS_KEY, JSON.stringify(posts));
  } catch {
    // ignore quota / private mode
  }
};

const readLocalHeartMap = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const raw = window.localStorage.getItem(LOCAL_HEARTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const writeLocalHeartMap = (map) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(LOCAL_HEARTS_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
};

export const getLocalHeartIds = (userId) => {
  if (!userId) return new Set();
  const list = readLocalHeartMap()[userId];
  return new Set(
    (Array.isArray(list) ? list : []).map((value) => String(value)).filter(Boolean)
  );
};

export const setLocalHeart = (userId, postId, liked) => {
  if (!userId || !postId) return;
  const map = readLocalHeartMap();
  const next = getLocalHeartIds(userId);
  if (liked) next.add(String(postId));
  else next.delete(String(postId));
  map[userId] = [...next];
  writeLocalHeartMap(map);
};

export const applyLocalHearts = (posts, userId) => {
  if (!Array.isArray(posts)) return [];
  if (!userId) return posts;
  const mine = getLocalHeartIds(userId);
  let changed = false;
  posts.forEach((post) => {
    if (post?.id && post.likedBy.includes(userId) && !mine.has(post.id)) {
      mine.add(post.id);
      changed = true;
    }
  });
  if (changed) {
    const map = readLocalHeartMap();
    map[userId] = [...mine];
    writeLocalHeartMap(map);
  }
  if (mine.size === 0) return posts;
  return posts.map((post) => {
    if (!post?.id || !mine.has(post.id) || post.likedBy.includes(userId)) {
      return post;
    }
    return { ...post, likedBy: [...post.likedBy, userId] };
  });
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number(value) || 0 : parsed;
};

export const normalizePost = (raw, id) => {
  if (!raw || typeof raw !== "object") return null;
  const gridSize = clampGridSize(raw.gridSize);
  if (!gridSize) return null;
  const likedBy = Array.isArray(raw.likedBy)
    ? raw.likedBy.map((value) => String(value)).filter(Boolean)
    : [];
  return {
    id: String(id || raw.id || ""),
    userId: String(raw.userId || ""),
    displayName: String(raw.displayName || "").trim(),
    gridSize,
    cells: String(raw.cells || ""),
    heartCount: Math.max(0, Math.floor(Number(raw.heartCount) || 0)),
    likedBy,
    createdAtMs: toMillis(raw.createdAt || raw.createdAtMs),
  };
};

export const postsForTab = (posts, tab) => {
  const cutoff = Date.now() - NEW_MAX_AGE_MS;
  const next = Array.isArray(posts) ? [...posts] : [];
  if (tab === "popular") {
    return next
      .filter((post) => post.heartCount > POPULAR_MIN_HEARTS)
      .sort((a, b) => {
        if (b.heartCount !== a.heartCount) return b.heartCount - a.heartCount;
        return b.createdAtMs - a.createdAtMs;
      })
      .slice(0, MAX_POPULAR);
  }
  if (tab === "new") {
    return next
      .filter((post) => !post.createdAtMs || post.createdAtMs > cutoff)
      .sort((a, b) => b.createdAtMs - a.createdAtMs)
      .slice(0, MAX_NEW);
  }
  return next
    .filter((post) => post.createdAtMs > 0 && post.createdAtMs <= cutoff)
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
    .slice(0, MAX_ALL);
};

export const postsForAlbum = (posts) =>
  [...(Array.isArray(posts) ? posts : [])]
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
    .slice(0, MAX_ALL);

export const publishPixelPost = async ({
  userId,
  displayName,
  gridSize,
  cells,
}) => {
  const size = clampGridSize(gridSize);
  if (!size || !userId) return null;

  const payload = {
    userId,
    displayName: String(displayName || "").trim(),
    gridSize: size,
    cells: String(cells || ""),
    heartCount: 0,
    likedBy: [],
    createdAt: db ? serverTimestamp() : new Date().toISOString(),
  };

  if (!db) {
    const post = normalizePost(
      { ...payload, createdAtMs: Date.now() },
      `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    );
    writeLocalPosts([post, ...readLocalPosts()]);
    return post;
  }

  const docRef = await addDoc(collection(db, PIXEL_POSTS_COLLECTION), payload);
  return normalizePost(
    { ...payload, createdAtMs: Date.now() },
    docRef.id
  );
};

export const togglePixelPostHeart = async ({ postId, userId, liked }) => {
  if (!postId || !userId) return null;
  const nextLiked = !liked;
  setLocalHeart(userId, postId, nextLiked);

  if (!db) {
    const posts = readLocalPosts().map((post) => {
      if (post.id !== postId) return post;
      const has = post.likedBy.includes(userId);
      if (nextLiked === has) return post;
      const likedBy = nextLiked
        ? [...post.likedBy, userId]
        : post.likedBy.filter((id) => id !== userId);
      return {
        ...post,
        likedBy,
        heartCount: Math.max(0, post.heartCount + (nextLiked ? 1 : -1)),
      };
    });
    writeLocalPosts(posts);
    return posts.find((post) => post.id === postId) || null;
  }

  await updateDoc(doc(db, PIXEL_POSTS_COLLECTION, postId), {
    heartCount: increment(nextLiked ? 1 : -1),
    likedBy: nextLiked ? arrayUnion(userId) : arrayRemove(userId),
  });
  return true;
};

export const deletePixelPost = async ({ postId, userId }) => {
  if (!postId || !userId) return false;

  if (!db) {
    const posts = readLocalPosts();
    const target = posts.find((post) => post.id === postId);
    if (!target || target.userId !== userId) return false;
    writeLocalPosts(posts.filter((post) => post.id !== postId));
    return true;
  }

  await deleteDoc(doc(db, PIXEL_POSTS_COLLECTION, postId));
  return true;
};

export const subscribePixelPosts = (tab, onChange) => {
  const apply = (posts) => onChange(postsForTab(posts, tab));

  if (!db) {
    apply(readLocalPosts());
    return () => {};
  }

  const cutoffDate = new Date(Date.now() - NEW_MAX_AGE_MS);
  let postsQuery;
  if (tab === "popular") {
    postsQuery = query(
      collection(db, PIXEL_POSTS_COLLECTION),
      where("heartCount", ">", POPULAR_MIN_HEARTS),
      orderBy("heartCount", "desc"),
      limit(MAX_POPULAR)
    );
  } else if (tab === "all") {
    postsQuery = query(
      collection(db, PIXEL_POSTS_COLLECTION),
      where("createdAt", "<=", cutoffDate),
      orderBy("createdAt", "desc"),
      limit(MAX_ALL)
    );
  } else {
    postsQuery = query(
      collection(db, PIXEL_POSTS_COLLECTION),
      where("createdAt", ">", cutoffDate),
      orderBy("createdAt", "desc"),
      limit(MAX_NEW)
    );
  }

  return onSnapshot(
    postsQuery,
    (snapshot) => {
      apply(
        snapshot.docs
          .map((docSnap) => normalizePost(docSnap.data(), docSnap.id))
          .filter(Boolean)
      );
    },
    (error) => {
      console.warn(
        `[Pixels] Could not listen to ${PIXEL_POSTS_COLLECTION}:`,
        error?.message || error
      );
      apply(readLocalPosts());
    }
  );
};

export const subscribeMyPixelPosts = (userId, onChange) => {
  if (!userId) {
    onChange([]);
    return () => {};
  }

  const mine = (posts) =>
    postsForAlbum(posts.filter((post) => post.userId === userId));

  if (!db) {
    onChange(mine(readLocalPosts()));
    return () => {};
  }

  const postsQuery = query(
    collection(db, PIXEL_POSTS_COLLECTION),
    where("userId", "==", userId),
    limit(MAX_ALL)
  );

  return onSnapshot(
    postsQuery,
    (snapshot) => {
      onChange(
        mine(
          snapshot.docs
            .map((docSnap) => normalizePost(docSnap.data(), docSnap.id))
            .filter(Boolean)
        )
      );
    },
    (error) => {
      console.warn(
        `[Pixels] Could not listen to my posts in ${PIXEL_POSTS_COLLECTION}:`,
        error?.message || error
      );
      onChange(mine(readLocalPosts()));
    }
  );
};
